import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector, useStore } from "react-redux";
import { Col, Row } from "react-bootstrap";

import { toast, setUserDetails } from "Layout/slice";
import { parseErrorMessage } from "utils";
import { change2FA, sendStepUpCode, verifyStepUpCode } from "../../../service";
import { codeInitialValues, codeValidationSchema } from "./validation";
import { Switch } from "components/Switch";
import { Button } from "components/Button";
import { Form, Input } from "components/Form";
import { Loader } from "components/Loader";
import { ChangePhone } from "../../Components/ChangePhone";
import { userDetailSelect } from "Layout/slice/selector";
import { Heading } from "style/components/Heading.styles";
import { WithdrawalVerificationContainer } from "./WithdrawalVerification.styles";

const RESEND_SECONDS = 20;

export const WithdrawalVerification = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const profileDetails = useSelector(userDetailSelect);

  // Organization-level switch for MFA as a whole, the same flag the 2FA
  // section gates on; `enabled` is only the user's own preference within it.
  const allowed = Boolean(profileDetails?.mfa_allowed);
  const enabled = Boolean(profileDetails?.withdrawal_mfa_enabled);

  // Shown when the user wants it on but has no verified phone to send to.
  const [showPhoneSetup, setShowPhoneSetup] = useState(false);
  // Shown when switching off: the backend requires a fresh code first.
  const [confirmingOff, setConfirmingOff] = useState(false);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (seconds <= 0) return undefined;
    const intervalId = setInterval(() => setSeconds((s) => s - 1), 1000);
    return () => clearInterval(intervalId);
  }, [seconds]);

  const notifyError = (error) => {
    dispatch(toast({ type: "error", body: parseErrorMessage(error) }));
  };

  const applyToggle = async (value, stepUpToken) => {
    const response = await change2FA({
      value: value ? "ON" : "OFF",
      scope: "withdrawal",
      ...(stepUpToken ? { step_up_token: stepUpToken } : {}),
    });
    // Keep Redux in step with the server so navigating away and back doesn't
    // show a stale switch. Read the user at dispatch time rather than from the
    // render closure: ChangePhone may have just written the verified number,
    // and setUserDetails replaces the whole object.
    dispatch(
      setUserDetails({
        ...userDetailSelect(store.getState()),
        withdrawal_mfa_enabled: response?.withdrawal_mfa_enabled ?? value,
      })
    );
    dispatch(
      toast({
        body: `Withdrawal verification has been ${
          value ? "enabled" : "disabled"
        } successfully!`,
      })
    );
  };

  const requestCode = async () => {
    await sendStepUpCode({ action: "mfa_disable" });
    setSeconds(RESEND_SECONDS);
    dispatch(toast({ type: "info", body: "The code has been sent!" }));
  };

  const handleSwitchChange = async (value) => {
    if (value) {
      // Turning on needs a verified phone, because that is where the
      // withdrawal codes will be sent.
      if (!profileDetails?.phone_verified) {
        setShowPhoneSetup(true);
        return;
      }
      try {
        await applyToggle(true);
      } catch (error) {
        notifyError(error);
      }
      return;
    }

    // Turning off is itself a protected action: a stolen session must not be
    // able to switch withdrawal protection off and then withdraw.
    try {
      await requestCode();
      setConfirmingOff(true);
    } catch (error) {
      notifyError(error);
    }
  };

  const handleConfirmOff = async (values) => {
    let stepUpToken;
    try {
      ({ step_up_token: stepUpToken } = await verifyStepUpCode({
        action: "mfa_disable",
        mfa_code: values.code.trim(),
      }));
    } catch (error) {
      // A rejected code leaves the form open so it can be retyped.
      notifyError(error);
      return;
    }

    // Past this point the code is spent, so close the form even if the toggle
    // itself fails — retrying needs a new code, not the one on screen.
    setConfirmingOff(false);
    codeFormik.resetForm();

    try {
      await applyToggle(false, stepUpToken);
    } catch (error) {
      notifyError(error);
    }
  };

  const handleCancelOff = () => {
    setConfirmingOff(false);
    codeFormik.resetForm();
  };

  const handleResend = async () => {
    try {
      await requestCode();
    } catch (error) {
      notifyError(error);
    }
  };

  const handlePhoneVerified = async () => {
    setShowPhoneSetup(false);
    try {
      await applyToggle(true);
    } catch (error) {
      notifyError(error);
    }
  };

  const codeFormik = useFormik({
    onSubmit: handleConfirmOff,
    initialValues: codeInitialValues,
    validationSchema: codeValidationSchema,
  });

  // Placed after every hook so the hook order stays stable when the flag
  // arrives with the profile.
  if (!allowed) return null;

  const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;
  const maskedPhone = profileDetails?.phone
    ? `••• ${String(profileDetails.phone).slice(-3)}`
    : "your phone";

  return (
    <WithdrawalVerificationContainer>
      <div className="switch-wrapper">
        <div className="heading-wrapper">
          <Heading>Withdrawal verification</Heading>
          <span>
            Require a code before money can leave your account, even if someone
            is already signed in
          </span>
        </div>
        <div className="text-message-wrapper">
          {/*
            Controlled: the switch shows the server's value, still reading ON
            while the disable code is being confirmed. A cancelled or failed
            toggle needs no correction, because it never moved.
          */}
          <Switch
            variant="secondary"
            size="small"
            onChange={handleSwitchChange}
            value={confirmingOff ? true : enabled}
          />
          <div className="text-message-inner-wrapper">
            <p className="mb-0">Text Message (SMS)</p>
            <span>
              Receive a one-time passcode each time you withdraw or change your
              bank.
            </span>
          </div>
        </div>
      </div>

      {confirmingOff && (
        <div className="step-up-wrapper">
          <p className="step-up-prompt">
            To turn this off, enter the code we sent to {maskedPhone}.
          </p>
          <Form className="step-up-form" onSubmit={codeFormik.handleSubmit}>
            <div className="code-input">
              <Input
                label="Otp code"
                type="text"
                placeholder="Code"
                {...codeFormik.getFieldProps("code")}
              />
              {codeFormik.touched.code && codeFormik.errors.code ? (
                <div className="form-error">{codeFormik.errors.code}</div>
              ) : null}
            </div>
            <div className="step-up-actions">
              <Button
                type="submit"
                disabled={!codeFormik.isValid || codeFormik.isSubmitting}
              >
                {codeFormik.isSubmitting ? (
                  <Loader className="on-btn" />
                ) : (
                  "Confirm"
                )}
              </Button>
              <Button
                type="button"
                className="button-outlined"
                disabled={seconds > 0}
                onClick={handleResend}
              >
                {seconds > 0 ? `Resend in ${formattedSeconds}` : "Resend"}
              </Button>
              <Button
                type="button"
                className="button-outlined"
                onClick={handleCancelOff}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </div>
      )}

      {showPhoneSetup && (
        <>
          <p className="no-phone-hint">
            Add and verify a phone number to receive withdrawal codes.
          </p>
          <Row className="mb-4">
            <Col md={4}>
              <ChangePhone onSuccess={handlePhoneVerified} />
            </Col>
          </Row>
        </>
      )}

      <div className="divider mt-1 mb-3"></div>
    </WithdrawalVerificationContainer>
  );
};
